import { KestrixToast, useToast as useToastState } from '@shared'
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo
} from 'react'

type ShowToast = (message: string, durationMs?: number) => void

type Toaster = {
  showError: ShowToast
  showSuccess: ShowToast
}

const ToastContext = createContext<Toaster | null>(null)

/**
 * Returns the functions that show a toast.
 *
 * @returns {Toaster} Shows one toast at a time. A new toast replaces the current one.
 * @throws {Error} If used outside of ToastProvider
 */
export const useToast = (): Toaster => {
  const toaster = useContext(ToastContext)
  if (!toaster) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return toaster
}

/**
 * Renders the toast and provides the functions that show it.
 *
 * The state lives above the login form so a toast survives the form remounting.
 */
export const ToastProvider = ({ children }: PropsWithChildren) => {
  const { toast, showSuccess, showError, hideToast } = useToastState()

  const toaster = useMemo(
    () => ({ showError, showSuccess }),
    [showError, showSuccess]
  )

  return (
    <ToastContext value={toaster}>
      {children}
      <KestrixToast
        // Remount per message so a new toast restarts the auto-hide timer
        key={toast.message}
        open={toast.open}
        message={toast.message}
        type={toast.type}
        duration={toast.duration}
        onClose={hideToast}
        testId={`login-toast-${toast.type}`}
      />
    </ToastContext>
  )
}
